import { useEffect, useState, useCallback } from "react";
import PageLoader from "../../components/pageLoader";
import Toast from "../../components/toast";
import ToastContainer from "../../components/toastContainer";
import { getOrders } from "../../api/ordersApi";
import { getEvents } from "../../api/eventsApi";
import { CURRENCY_MAP } from "../../constants";
import { useNavigate } from "react-router-dom";
import { SearchIcon, FilterIcon, XIcon } from "@heroicons/react/outline";

const OrdersPage = () => {
  const [limit, setLimit] = useState(10);
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [eventFilter, setEventFilter] = useState("");
  const [events, setEvents] = useState([]);

  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState(null);

  const totalPages = Math.ceil(total / limit);

  const getPageNumbers = () => {
    const delta = 2;
    const range = [];
    const left = Math.max(2, page - delta);
    const right = Math.min(totalPages - 1, page + delta);
    range.push(1);
    if (left > 2) range.push("...");
    for (let i = left; i <= right; i++) range.push(i);
    if (right < totalPages - 1) range.push("...");
    if (totalPages > 1) range.push(totalPages);
    return range;
  };

  const formatCurrency = (amount, currency) => {
    const symbol = CURRENCY_MAP[currency] || currency;
    return `${symbol} ${parseFloat(amount).toFixed(2)}`;
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError("");
      const offset = (page - 1) * limit;
      const data = await getOrders(limit, offset, search, eventFilter);
      setOrders(data.results || []);
      setTotal(data.count || 0);
    } catch (err) {
      setError("Failed to load orders.");
    } finally {
      setLoading(false);
    }
  };

  const fetchEvents = async () => {
    try {
      const data = await getEvents(100, 0);
      setEvents(data.results || []);
    } catch (err) {
      // silently fail — filter just won't populate
    }
  };

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    fetchOrders();
  }, [page, limit, search, eventFilter]);

  useEffect(() => {
    fetchEvents();
  }, []);

  const handleLimitChange = (e) => {
    setPage(1);
    setLimit(Number(e.target.value));
  };

  const handleEventFilter = (e) => {
    setEventFilter(e.target.value);
    setPage(1);
  };

  const clearSearch = () => {
    setSearchInput("");
    setSearch("");
    setPage(1);
  };

  const statusStyles = {
    paid: "bg-emerald-500/10 text-emerald-400",
    expired: "bg-amber-500/10 text-amber-400",
    failed: "bg-red-500/10 text-red-400",
    refunded: "bg-slate-500/10 text-slate-400",
  };

  const statusClass = (status) =>
    statusStyles[(status || "").toLowerCase()] ||
    "bg-slate-500/10 text-slate-400";

  if (loading && orders.length === 0) {
    return <PageLoader />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white">Orders</h1>
          <p className="text-sm text-slate-400">
            Manage Orders information and details
          </p>
        </div>
      </div>

      {error && <div className="p-6 text-red-400">{error}</div>}

      {/* ── Search + Filter bar ── */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Search */}
        <div className="relative flex-1">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by order ID, name or email..."
            className="w-full bg-slate-900/60 border border-white/10 rounded-lg pl-9 pr-9 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-pink-500/40"
          />
          {searchInput && (
            <button
              onClick={clearSearch}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white transition"
            >
              <XIcon className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Event filter */}
        <div className="relative sm:w-64">
          <FilterIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
          <select
            value={eventFilter}
            onChange={handleEventFilter}
            className="w-full bg-slate-900/60 border border-white/10 rounded-lg pl-9 pr-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40 cursor-pointer appearance-none"
          >
            <option value="">All events</option>
            {events.map((ev) => (
              <option key={ev.id} value={ev.id}>
                {ev.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-slate-900/60 border border-white/10 rounded-xl">
        <div className="overflow-x-auto">
          <table className="min-w-[900px] w-full border-collapse">
            <thead>
              <tr className="border-b border-white/10 text-center text-xs uppercase tracking-wider text-slate-400">
                <th className="px-6 py-4">Order #</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4">Event</th>
                <th className="px-6 py-4">Customer Details</th>
                <th className="px-6 py-4">Type</th>
                <th className="px-6 py-4">Total</th>
              </tr>
            </thead>

            <tbody>
              {orders.map((od) => (
                <tr
                  key={od.id}
                  onClick={() => navigate(`/dashboard/orders/${od.id}`)}
                  className="border-b border-white/5 hover:bg-slate-800/40 transition cursor-pointer text-center"
                >
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-slate-300">
                      {od.order_number}
                    </div>
                  </td>

                  <td className="px-6 py-4">
                    <span
                      className={`px-2.5 py-0.5 rounded-md text-xs font-medium capitalize ${statusClass(od.status)}`}
                    >
                      {od.status || "-"}
                    </span>
                  </td>

                  <td className="px-6 py-4 text-sm text-slate-300">
                    {od.event?.name || "-"}
                  </td>

                  <td className="px-6 py-4 text-sm text-slate-300">
                    {od.full_name} <br />
                    <span className="text-slate-500 text-xs">{od.email}</span>
                  </td>

                  <td className="px-6 py-4 text-sm text-slate-300">
                    {od.type || "-"}
                  </td>

                  <td className="px-6 py-4 text-sm text-slate-300">
                    {formatCurrency(od.total, od.currency) || "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {orders.length === 0 && !loading && (
          <div className="p-8 text-center text-slate-400">No orders found.</div>
        )}
      </div>

      {/* Pagination */}
      <div className="flex justify-end">
        <div className="flex items-center gap-3 px-6 py-4 text-sm text-slate-400">
          <span className="text-slate-400">Rows per page:</span>
          <select
            value={limit}
            onChange={handleLimitChange}
            className="bg-slate-800 text-slate-300 border border-white/10 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-pink-500 cursor-pointer"
          >
            {[5, 10, 25, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>

          {totalPages > 1 && (
            <>
              <button
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
                className={`px-3 py-1 rounded-md border border-white/10 transition ${
                  page === 1
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-slate-800 text-white"
                }`}
              >
                Previous
              </button>

              <div className="flex gap-1">
                {getPageNumbers().map((pageNumber, index) =>
                  pageNumber === "..." ? (
                    <span
                      key={`ellipsis-${index}`}
                      className="px-3 py-1 text-slate-500 select-none"
                    >
                      ...
                    </span>
                  ) : (
                    <button
                      key={pageNumber}
                      onClick={() => setPage(pageNumber)}
                      className={`px-3 py-1 rounded-md border border-white/10 transition ${
                        page === pageNumber
                          ? "bg-pink-600 text-white"
                          : "text-slate-300 hover:bg-slate-800 hover:text-white"
                      }`}
                    >
                      {pageNumber}
                    </button>
                  ),
                )}
              </div>

              <button
                disabled={page === totalPages}
                onClick={() => setPage((p) => p + 1)}
                className={`px-3 py-1 rounded-md border border-white/10 transition ${
                  page === totalPages
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-slate-800 text-white"
                }`}
              >
                Next
              </button>
            </>
          )}
        </div>
      </div>

      <ToastContainer>
        {toast && (
          <div className="toast-enter">
            <Toast
              type={toast.type}
              message={toast.message}
              onClose={() => setToast(null)}
            />
          </div>
        )}
      </ToastContainer>
    </div>
  );
};

export default OrdersPage;
